import { memoize } from 'lodash';
import {
  getStoredItem,
  removeStoredItem,
  setStoredItem,
} from '@libertify.frontend/helpers';
import { AddOnType, MediaProjectInstanceResponse } from './types';
import {
  ACCESS_REQUEST_KEY,
  APP_PREFIX,
  CUSTOM_CHAT_LABEL_PROJECTS_ID,
  CUSTOM_PROJECTS_ID,
  CUSTOM_STYLES_ORGANIZATIONS_ID,
} from './constants';

export const getIsEmbed = (
  params: { type?: string },
  embedParam: string | null,
): boolean => params?.type === 'embed' || Boolean(embedParam);
/** Pass a RESOLVED list — an in-flight read looks like "no add-ons" here. */
export const hasChatAddOn = (enabledAddOns: AddOnType[] = []): boolean =>
  enabledAddOns.includes('Chatbot');

/**
 * The LEGACY experience's chat gate: the instance's own `enable_chat` setting.
 *
 * Deliberately does NOT look at `enabled_add_ons` — those projects predate
 * chat-as-an-add-on and their instance setting is the only thing the creator
 * ever set. The new experience route reads `hasChatAddOn(enabled_add_ons)`
 * instead, with no fallback to this.
 */
export const hasChat = (publication?: MediaProjectInstanceResponse) =>
  publication?.enable_chat ?? true;

/**
 * Check for the feature flag instance settings include_document_animations
 * If false, the PDF document is hidden.
 * @param publication The publication object
 * @returns boolean
 */
export const hasDocumentAnimation = (
  publication?: MediaProjectInstanceResponse,
) => {
  // if include_document_animations is false, document_animation = null
  // for new highlights model (without cuepoints file), document_animation.cuepoints is always null
  return Boolean(publication?.document_animation);
};

/**
 * Check for the feature flag of chat_answers. If false, the chat is visible, but the bot replies only include source cards and not its conversational output.
 * @param publication The publication object
 * @returns boolean
 */
export const hasChatAnswers = (publication?: MediaProjectInstanceResponse) => {
  return publication?.enable_chat_answers ?? true;
};

export const videoOnly = (publication?: MediaProjectInstanceResponse) => {
  return !hasDocumentAnimation(publication) && !hasChat(publication);
};

// Prompt projects are detected by their first document having a
// DocumentGeneratorProvider retrieval — same signal used by the admin app
// (slides-streaming.tsx, ProjectSettingsDrawer.tsx). Depends on the
// publication endpoint serializing `documents[*].retrieval.provider`; if
// the backend strips that field this returns false and we fall back to the
// default chat layout.
export const isPromptProject = (
  publication?: MediaProjectInstanceResponse,
): boolean => {
  const firstDoc = Object.values(publication?.documents ?? {})[0];
  return firstDoc?.retrieval?.provider?.type === 'DocumentGeneratorProvider';
};
export const getLegacyRoutes = () => {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get('projectId');
  const publicationId = params.get('publicationId');
  params.delete('projectId');
  params.delete('publicationId');
  // default to demos ? not a good idea when we share the domain
  return { projectId, publicationId, params };
};

export const shouldShowLanguageSelector = (projectId?: string) => {
  if (!projectId) return true;

  const excludedProjects = [
    ...Object.values(CUSTOM_PROJECTS_ID.hkinvest_prod),
    CUSTOM_PROJECTS_ID.hkinvest_staging,
    CUSTOM_PROJECTS_ID.hkinvest_dev,
  ];

  return !excludedProjects.includes(projectId);
};

export const shouldShowLogo = (projectId?: string) => {
  if (!projectId) return true;

  const excludedProjects = [
    ...Object.values(CUSTOM_PROJECTS_ID.hkinvest_prod),
    CUSTOM_PROJECTS_ID.hkinvest_staging,
    CUSTOM_PROJECTS_ID.hkinvest_dev,
  ];
  return !excludedProjects.includes(projectId);
};
export const shouldShowLeadForm = (projectId?: string) => {
  if (!projectId) return true;

  const projects = [CUSTOM_PROJECTS_ID.alcyon];
  return projects.includes(projectId);
};
export const shouldLoadGtmScript = (projectId?: string) => {
  // Unknown project (PIN, password, error screens): never load the hkinvest GTM
  if (!projectId) return false;

  const projects = [
    ...Object.values(CUSTOM_PROJECTS_ID.hkinvest_prod),
    CUSTOM_PROJECTS_ID.hkinvest_staging,
    CUSTOM_PROJECTS_ID.hkinvest_dev,
  ];
  return projects.includes(projectId);
};
export const hasCustomStyles = memoize((organizationId: string) =>
  Object.values(CUSTOM_STYLES_ORGANIZATIONS_ID).includes(organizationId),
);

// MED-6911 — whether this project uses the custom chat-trigger label.
export const hasCustomChatLabel = memoize(
  (projectId?: string): boolean =>
    !!projectId &&
    Object.values(CUSTOM_CHAT_LABEL_PROJECTS_ID).includes(projectId),
);

/**
 * Resolve a CallToAction's display label for the current locale, mirroring
 * the classic CallToActionList's `getLabel`: exact locale match, then
 * `en-US`, then the single defined language if only one was set (admin lets
 * orgs leave some languages blank), then the caller's fallback.
 */
export const resolveCallToActionLabel = (
  labels: Record<string, string> | undefined,
  locale: string,
  fallback: string,
): string => {
  const nonEmptyValues = Object.values(labels ?? {}).filter(Boolean);
  const uniqueFallback = nonEmptyValues.length === 1 ? nonEmptyValues[0] : '';
  return labels?.[locale] || labels?.['en-US'] || uniqueFallback || fallback;
};

// Convert language code (used in i18next) to full locale code (used in CTAs, etc)
export const getFullLocale = (language: string) => {
  const langMapping: Record<string, string> = {
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    // es: 'es-ES',
    // it: 'it-IT',
    // ar: 'ar-SA',
    // pt: 'pt-PT',
  };
  const finalLocale =
    langMapping[language] || `${language}-${language.toUpperCase()}`;
  return finalLocale;
};

export const REQUEST_ACCESS_STORAGE_KEY = APP_PREFIX + '_' + ACCESS_REQUEST_KEY;

// Mirrors PIN_CACHE_EXPIRE: the emailed PIN is only valid for 15 minutes.
export const REQUEST_ACCESS_MAX_AGE_DAYS = 15 / (60 * 24);

export const getRequestAccessData = () => {
  if (globalThis.window === undefined) return {};
  try {
    // Strict: entries without `expires` are dropped and removed.
    const raw = getStoredItem(REQUEST_ACCESS_STORAGE_KEY, { strict: true });
    return JSON.parse(raw ?? '{}');
  } catch {
    return {};
  }
};

export const setRequestAccessData = (data: Record<string, unknown>) => {
  setStoredItem(
    REQUEST_ACCESS_STORAGE_KEY,
    JSON.stringify(data),
    REQUEST_ACCESS_MAX_AGE_DAYS,
  );
};

export const removeRequestAccessData = () => {
  removeStoredItem(REQUEST_ACCESS_STORAGE_KEY);
};

/**
 * Toggle the 'playing' class on the body element to show/hide header elements.
 * @param show - Whether to show the header (removes 'playing' class) or hide it (adds 'playing' class)
 */
export const handleShowHeader = (show: boolean) => {
  const body = document.querySelector('body') as HTMLElement;
  if (!body) return;

  if (show) {
    body.classList.remove('playing');
  } else {
    body.classList.add('playing');
  }
};
